import { useAppDispatch, useAppSelector } from "@common/reducers/hooks/store.hook";
import { setDueDate, setIsRecurring, setRruleset, setTimeInputInvalid, markDueDateSetByUser } from "@common/reducers/ScheduledMessageEditorReducer";
import { selectDefaultSchedulingTime, selectCurrentWorkspace } from "@common/reducers/UserSelectors";
import { getRuleOptionsSafe, buildTimeEditedRuleString, resolveStampingTzid } from "./recurrence-utils";
import moment from "moment";
import { useMemo, useCallback, useRef, useEffect, type ReactNode } from "react";
import BTTimePicker from "@extension/context/content/components/bt-bulk-dashboard/time-and-date/bt-time-picker";
import { resolveDisplayTime } from "./time-input-utils";
import { THEMED_TIME_PICKER_COLORS } from "@extension/context/content/components/bt-bulk-dashboard/time-and-date/picker-colors";

const ScheduleTimePicker = ({ trigger }: { trigger?: ReactNode } = {}) => {
    const dispatch = useAppDispatch();
    const dueDate = useAppSelector((state: any) => state.ScheduledMessageEditorReducer.dueDate);
    const rruleset = useAppSelector((state: any) => state.ScheduledMessageEditorReducer.rruleset);
    const isRecurring = useAppSelector((state: any) => state.ScheduledMessageEditorReducer.isRecurring);
    const defaultSchedulingTime = useAppSelector(selectDefaultSchedulingTime);
    // BE#648 (B1): editing the time on a migrated TZID series must stamp the workspace
    // zone + wall-clock (not UTC); no workspace zone -> legacy UTC write.
    const workspaceTimezone = useAppSelector(selectCurrentWorkspace)?.timezone;
    // B1 side effect: an EXISTING rule's OWN embedded TZID always wins — a viewer's
    // zone must never re-zone a Jerusalem rule to London, nor drop the TZID when the
    // viewer has none. FE#5.9.28: a brand-new (no-TZID) rule is seeded from the
    // VIEWER's browser zone first (→ workspace tz → legacy), via the shared resolver.
    const effectiveTz = resolveStampingTzid(rruleset, workspaceTimezone);

    const ruleOptions = useMemo(
        () => getRuleOptionsSafe(rruleset, dueDate, effectiveTz),
        [dueDate, rruleset, effectiveTz]
    );

    // Track last value sent to prevent loops
    const lastSentValueRef = useRef<string>('');

    // FE#260: the displayed time follows the LIVE dueDate; the saved
    // defaultSchedulingTime preference only SEEDS the value when there is no
    // dueDate yet. The old code returned the fixed preference and ignored
    // dueDate, so a user with a default time set could never change the field
    // (their edits didn't stick). See resolveDisplayTime for the precedence.
    const timeValue = useMemo(
        () => resolveDisplayTime(dueDate, defaultSchedulingTime),
        [dueDate, defaultSchedulingTime]
    );

    // Sync ref when value changes externally (from Redux) - but only if different
    useEffect(() => {
        if (timeValue && timeValue !== lastSentValueRef.current) {
            lastSentValueRef.current = timeValue;
        }
    }, [timeValue]);

    const handleTimeChange = useCallback((timeString: string) => {
        // Prevent loops - if this is the same value we just sent, ignore it
        if (timeString === lastSentValueRef.current) {
            dispatch(markDueDateSetByUser()); // BE#657 M2 — confirming the shown time counts as a pick
            return;
        }
        
        // timeString is in HH:mm format
        const parts = timeString.split(':');
        if (parts.length !== 2) return;
        
        const [hours, minutes] = parts.map(Number);
        if (isNaN(hours) || isNaN(minutes)) return;
        if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return;
        
        // Get current date from existing dueDate, or use current date
        const existingMoment = dueDate ? moment.utc(dueDate) : moment();
        const existingLocal = existingMoment.local();
        
        // Create new moment with updated time in local timezone
        const newLocalMoment = moment()
            .year(existingLocal.year())
            .month(existingLocal.month())
            .date(existingLocal.date())
            .hour(hours)
            .minute(minutes)
            .second(0)
            .millisecond(0);
        
        if (!newLocalMoment.isValid()) return;
        
        // Convert to UTC for storage
        const newDueDateString = newLocalMoment.utc().toISOString();
        
        // Update ref before dispatching
        lastSentValueRef.current = timeString;
        
        try {
            if (isRecurring && rruleset) {
                dispatch(setRruleset(buildTimeEditedRuleString(ruleOptions, newDueDateString, effectiveTz)));
            }
            dispatch(setDueDate(newDueDateString));
        } catch (error) {
            console.error("Failed to update time:", error);
            dispatch(setDueDate(newDueDateString));
            if (isRecurring) {
                dispatch(setRruleset(''));
                dispatch(setIsRecurring(false));
            }
        }
    }, [dueDate, dispatch, isRecurring, ruleOptions, rruleset, effectiveTz]);

    // FE#312: mirror the time input's validity into Redux so the dialog can keep
    // the submit button disabled while a partial/invalid time is typed (instead of
    // auto-correcting it mid-typing).
    const handleTimeValidity = useCallback((isValid: boolean) => {
        dispatch(setTimeInputInvalid(!isValid));
    }, [dispatch]);

    // Clear the flag when this picker unmounts so a stale invalid state can't
    // linger and block a later, unrelated schedule.
    useEffect(() => () => {
        dispatch(setTimeInputInvalid(false));
    }, [dispatch]);

    return (
        <BTTimePicker
            value={timeValue}
            onChange={handleTimeChange}
            onValidityChange={handleTimeValidity}
            // CSS variables, not resolved colours — see picker-colors.ts.
            {...THEMED_TIME_PICKER_COLORS}
            trigger={trigger}
            seedDefaultOnMount={false}
        />
    );
};

export default ScheduleTimePicker;